import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FileText } from 'lucide-react';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { useWallet } from '../../hooks/useWallet';
import { agreementsAPI } from '../../services/api';
import {
  proposeAgreement,
  countersignAgreement,
  activateAgreement,
  fulfillAgreement,
  raiseDispute,
  cancelAgreement,
} from '../../services/blockchainClient';
import { AGREEMENT_STATUS, AGREEMENT_STATUS_LABELS } from '../../config/contracts';

const emptyPropose = {
  dataAssetId: '',
  serviceId: '',
  termsHash: '',
  euBsoContribution: false,
};

const statusClass = (status) => {
  if (status === AGREEMENT_STATUS.ACTIVE || status === AGREEMENT_STATUS.FULFILLED) return 'active';
  if (status === AGREEMENT_STATUS.DISPUTED || status === AGREEMENT_STATUS.CANCELLED || status === AGREEMENT_STATUS.EXPIRED) return 'inactive';
  return 'pending';
};

const refreshNotifications = () => {
  window.dispatchEvent(new Event('notifications:refresh'));
};

const getShortTxErrorMessage = (error, fallbackMessage) => {
  const message = String(
    error?.response?.data?.error ||
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    fallbackMessage
  );
  const lower = message.toLowerCase();
  if (lower.includes('insufficient funds')) return 'Insufficient funds';
  if (lower.includes('out of gas') || lower.includes('intrinsic gas')) return 'Gas too low';
  if (lower.includes('timeout') || lower.includes('timed out')) return 'Confirmation timed out';
  if (lower.includes('revert') || lower.includes('call exception')) return 'Reverted by contract';
  return message;
};

/**
 * Agreement lifecycle management panel (propose, countersign, activate, fulfill, dispute, cancel).
 */
export const AgreementPanel = () => {
  const { signer, isConnected, isNetworkMismatch } = useWallet();

  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [proposeForm, setProposeForm] = useState(emptyPropose);
  const [actionLoading, setActionLoading] = useState(null);
  const [disputeModal, setDisputeModal] = useState({ open: false, agreementId: null, reason: '' });

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agreementsAPI.listAgreements();
      setAgreements(data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const requireWallet = () => {
    if (!isConnected) { toast.error('Connect wallet first'); return false; }
    if (isNetworkMismatch) { toast.error('Switch to expected network first'); return false; }
    return true;
  };

  const handlePropose = async () => {
    if (!requireWallet()) return;
    if (!proposeForm.dataAssetId || !proposeForm.serviceId) {
      toast.error('Data Asset ID and Service ID are required');
      return;
    }
    setProposing(true);
    try {
      const termsBytes = proposeForm.termsHash || `0x${'0'.repeat(64)}`;
      toast.info('Transaction submitted: Please confirm the agreement proposal in your wallet.');
      const tx = await proposeAgreement({
        signer,
        dataAssetId: Number(proposeForm.dataAssetId),
        serviceId: Number(proposeForm.serviceId),
        termsHash: termsBytes,
        euBsoContribution: proposeForm.euBsoContribution,
      });
      const agreement = await agreementsAPI.createAgreement({
        on_chain_id: tx.agreementId,
        data_asset_id: Number(proposeForm.dataAssetId),
        service_id: Number(proposeForm.serviceId),
        terms_hash: proposeForm.termsHash || null,
        eu_bso_contribution: proposeForm.euBsoContribution,
        transaction_hash: tx.txHash,
        status: AGREEMENT_STATUS.PENDING,
      });
      toast.success(`Agreement pending on-chain: Agreement #${agreement.on_chain_id ?? agreement.id} is pending.`);
      refreshNotifications();
      setShowProposeModal(false);
      setProposeForm(emptyPropose);
      await fetchAgreements();
    } catch (error) {
      toast.error(`Transaction failed: ${getShortTxErrorMessage(error, 'Failed to propose agreement')}`);
    } finally {
      setProposing(false);
    }
  };

  const performAction = async (loadingKey, fn) => {
    if (!requireWallet()) return;
    setActionLoading(loadingKey);
    try {
      await fn();
      await fetchAgreements();
    } catch (error) {
      toast.error(`Transaction failed: ${getShortTxErrorMessage(error, 'Action failed')}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCountersign = (a) =>
    performAction(`countersign-${a.id}`, async () => {
      const chainId = a.on_chain_id ?? a.id;
      toast.info('Transaction submitted: Please confirm the countersign transaction.');
      const tx = await countersignAgreement({ signer, agreementId: chainId });
      const agreement = await agreementsAPI.updateAgreementStatus(a.id, AGREEMENT_STATUS.COUNTERSIGNED, tx.txHash);
      toast.success(`Agreement countersigned on-chain: Agreement #${agreement.on_chain_id ?? agreement.id} was countersigned.`);
      refreshNotifications();
    });

  const handleActivate = (a) =>
    performAction(`activate-${a.id}`, async () => {
      const chainId = a.on_chain_id ?? a.id;
      toast.info('Transaction submitted: Please confirm agreement activation.');
      const tx = await activateAgreement({ signer, agreementId: chainId });
      const agreement = await agreementsAPI.updateAgreementStatus(a.id, AGREEMENT_STATUS.ACTIVE, tx.txHash);
      toast.success(`Agreement activated on-chain: Agreement #${agreement.on_chain_id ?? agreement.id} is now active.`);
      refreshNotifications();
    });

  const handleFulfill = (a) =>
    performAction(`fulfill-${a.id}`, async () => {
      const chainId = a.on_chain_id ?? a.id;
      toast.info('Transaction submitted: Please confirm agreement fulfillment.');
      const tx = await fulfillAgreement({ signer, agreementId: chainId });
      const agreement = await agreementsAPI.updateAgreementStatus(a.id, AGREEMENT_STATUS.FULFILLED, tx.txHash);
      toast.success(`Agreement fulfilled on-chain: Agreement #${agreement.on_chain_id ?? agreement.id} was fulfilled.`);
      refreshNotifications();
    });

  const handleDispute = async () => {
    const { agreementId, dbId, reason } = disputeModal;
    if (!reason.trim()) { toast.error('Please enter a dispute reason'); return; }
    setDisputeModal({ open: false, agreementId: null, dbId: null, reason: '' });
    await performAction(`dispute-${dbId}`, async () => {
      toast.info('Transaction submitted: Please confirm the dispute transaction.');
      const tx = await raiseDispute({ signer, agreementId, reason: reason.trim() });
      const agreement = await agreementsAPI.updateAgreementStatus(dbId, AGREEMENT_STATUS.DISPUTED, tx.txHash);
      toast.success(`Agreement disputed on-chain: Agreement #${agreement.on_chain_id ?? agreement.id} is disputed.`);
      refreshNotifications();
    });
  };

  const handleCancel = (a) =>
    performAction(`cancel-${a.id}`, async () => {
      const chainId = a.on_chain_id ?? a.id;
      toast.info('Transaction submitted: Please confirm agreement cancellation.');
      const tx = await cancelAgreement({ signer, agreementId: chainId });
      const agreement = await agreementsAPI.updateAgreementStatus(a.id, AGREEMENT_STATUS.CANCELLED, tx.txHash);
      toast.success(`Agreement cancelled on-chain: Agreement #${agreement.on_chain_id ?? agreement.id} was cancelled.`);
      refreshNotifications();
    });

  const getActions = (a) => {
    const busy = !!actionLoading;
    const status = a.status;
    return (
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        {status === AGREEMENT_STATUS.PENDING && (
          <AppButton size="sm" variant="primary" disabled={busy} onClick={() => handleCountersign(a)}>
            Countersign
          </AppButton>
        )}
        {status === AGREEMENT_STATUS.COUNTERSIGNED && (
          <AppButton size="sm" variant="primary" disabled={busy} onClick={() => handleActivate(a)}>
            Activate
          </AppButton>
        )}
        {status === AGREEMENT_STATUS.ACTIVE && (
          <>
            <AppButton size="sm" variant="primary" disabled={busy} onClick={() => handleFulfill(a)}>
              Fulfill
            </AppButton>
            <AppButton
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                setDisputeModal({ open: true, agreementId: a.on_chain_id ?? a.id, dbId: a.id, reason: '' })
              }
            >
              Dispute
            </AppButton>
          </>
        )}
        {(status === AGREEMENT_STATUS.PENDING || status === AGREEMENT_STATUS.COUNTERSIGNED) && (
          <AppButton size="sm" variant="ghost" disabled={busy} onClick={() => handleCancel(a)}>
            Cancel
          </AppButton>
        )}
      </div>
    );
  };

  return (
    <section className="panel-card" aria-labelledby="agreements-title">
      <div className="panel-header">
        <h2 id="agreements-title">Data Service Agreements</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={fetchAgreements} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
            Refresh
          </button>
          <AppButton type="button" variant="primary" onClick={() => setShowProposeModal(true)}>
            Propose Agreement
          </AppButton>
        </div>
      </div>

      {loading ? (
        <SkeletonLoader rows={4} />
      ) : agreements.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No agreements yet"
          message="Propose an agreement using the button above to get started."
        />
      ) : (
        <div className="offer-grid">
          {agreements.map((a) => {
            const statusLabel = AGREEMENT_STATUS_LABELS[a.status] ?? `Status ${a.status}`;
            return (
              <article key={a.id} className="offer-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3>Agreement #{a.on_chain_id ?? '—'}</h3>
                  <span className={`status-badge ${statusClass(a.status)}`}>{statusLabel}</span>
                </div>
                <p><strong>Data Asset:</strong> {a.data_asset_id}</p>
                <p><strong>Service:</strong> {a.service_id}</p>
                {a.consumer && <p><strong>Consumer:</strong> {a.consumer.slice(0, 10)}…</p>}
                {a.provider && <p><strong>Provider:</strong> {a.provider.slice(0, 10)}…</p>}
                {a.escrow_amount && Number(a.escrow_amount) > 0 && (
                  <p><strong>Escrow:</strong> {a.escrow_amount} LBT</p>
                )}
                {a.eu_bso_contribution && (
                  <span className="status-badge active" style={{ fontSize: '0.7rem' }}>EU BSO</span>
                )}
                {a.transaction_hash && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted, #888)' }}>
                    Tx: {a.transaction_hash.slice(0, 18)}…
                  </p>
                )}
                {isConnected && getActions(a)}
              </article>
            );
          })}
        </div>
      )}

      {/* Propose modal */}
      <AppModal
        open={showProposeModal}
        title="Propose Agreement"
        onClose={() => setShowProposeModal(false)}
        footer={
          <>
            <AppButton variant="ghost" onClick={() => setShowProposeModal(false)}>Cancel</AppButton>
            <AppButton variant="primary" onClick={handlePropose} disabled={proposing}>
              {proposing ? 'Proposing…' : 'Submit Proposal'}
            </AppButton>
          </>
        }
      >
        <div className="form-grid">
          <label>
            Data Asset ID
            <input
              type="number"
              value={proposeForm.dataAssetId}
              onChange={(e) => setProposeForm((f) => ({ ...f, dataAssetId: e.target.value }))}
              placeholder="On-chain asset ID"
            />
          </label>
          <label>
            Service ID
            <input
              type="number"
              value={proposeForm.serviceId}
              onChange={(e) => setProposeForm((f) => ({ ...f, serviceId: e.target.value }))}
              placeholder="On-chain service ID"
            />
          </label>
          <label>
            Terms Hash (optional)
            <input
              value={proposeForm.termsHash}
              onChange={(e) => setProposeForm((f) => ({ ...f, termsHash: e.target.value }))}
              placeholder="0x… (leave blank for empty)"
            />
          </label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={proposeForm.euBsoContribution}
              onChange={(e) => setProposeForm((f) => ({ ...f, euBsoContribution: e.target.checked }))}
            />
            EU BSO Contribution
          </label>
        </div>
      </AppModal>

      {/* Dispute modal */}
      <AppModal
        open={disputeModal.open}
        title="Raise Dispute"
        onClose={() => setDisputeModal({ open: false, agreementId: null, dbId: null, reason: '' })}
        footer={
          <>
            <AppButton
              variant="ghost"
              onClick={() => setDisputeModal({ open: false, agreementId: null, dbId: null, reason: '' })}
            >
              Cancel
            </AppButton>
            <AppButton variant="primary" onClick={handleDispute}>
              Submit Dispute
            </AppButton>
          </>
        }
      >
        <label>
          Reason
          <textarea
            value={disputeModal.reason}
            onChange={(e) => setDisputeModal((prev) => ({ ...prev, reason: e.target.value }))}
            rows={4}
            placeholder="Describe the reason for the dispute…"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--ui-border, #ddd)', marginTop: '0.25rem', resize: 'vertical' }}
          />
        </label>
      </AppModal>
    </section>
  );
};
